<template>
  <v-main class="bg-content-timernew">
    <div class="wrap" :data-theme="isDark ? '' : 'light'">
      <!-- Masthead -->
      <v-container grid-list-xl fluid style="padding: 0px; top: 118px">
        <v-layout row wrap>
          <v-flex xs12>
            <v-card class="theme-bg-card-2 card-animate-2 masthead-card">
              <v-card-text>
                <div class="header-grid">
                  <div class="brand">
                    <div class="logo">
                      <img src="@/assets/cogneetiv-logo.png" alt="Cogneetiv AI Logo" />
                    </div>
                    <div>
                      <div class="title">Cogneetiv AI</div>
                      <div class="subtitle">
                        Timer • Sessions Log • Chart • Beep • Dark Mode • Voice • AI Coach
                      </div>
                    </div>
                  </div>

                  <div class="header-actions">
                    <div class="tog" title="Toggle dark mode">
                      <div>Dark</div>
                      <div
                        class="switch"
                        :class="{ on: isDark }"
                        role="switch"
                        :aria-checked="String(isDark)"
                        tabindex="0"
                        @click="toggleTheme"
                        @keydown.enter.prevent="toggleTheme"
                        @keydown.space.prevent="toggleTheme"
                      />
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-flex>

          <!-- LEFT COLUMN: AI Coach + Chart -->
          <v-flex xs12 md6>
            <v-layout column>
              <!-- AI Coach -->
              <v-flex>
                <v-card class="theme-bg-card-2 card-animate-2">
                  <v-card-title class="card-head">
                    <div class="card-title">AI Coach</div>
                    <div class="actions">
                      <button class="btn ghost" @click="toggleListening" :aria-pressed="String(isListening)">
                        {{ isListening ? '🛑 Stop' : '🎙️ Listen' }}
                      </button>
                      <button class="btn ghost" @click="speakHint" title="Voice hint">🔊</button>
                    </div>
                  </v-card-title>

                  <v-card-text>
                    <div class="coach-grid">
                      <input
                        type="text"
                        v-model.trim="coachQuestion"
                        placeholder="e.g., What patterns do you see in my last 14 days?"
                        class="field-input"
                      />
                      <div class="coach-actions">
                        <button class="btn primary" @click="askCoach" :disabled="coachLoading">
                          {{ coachLoading ? 'Thinking…' : 'Ask AI' }}
                        </button>
                        <button class="btn" @click="applyVoiceInsights" :disabled="!lastVoiceText">
                          Use last voice note
                        </button>
                        <span v-if="lastVoiceText" class="hint ellipsis">Heard: “{{ lastVoiceText }}”</span>
                      </div>

                      <div v-if="coachAnswer" class="ai-answer" v-html="coachAnswer"></div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-flex>

              <!-- Chart -->
              <v-flex>
                <v-card class="theme-bg-card-2 card-animate-2">
                  <v-card-title class="card-head">
                    <div class="card-title">Last 14 days — Focus sessions</div>
                    <div class="legend"><span class="dot focus"></span> Focus</div>
                  </v-card-title>
                  <v-card-text>
                    <div class="chart-body">
                      <svg ref="chart" viewBox="0 0 660 180" width="100%" height="180" aria-label="Sessions chart"></svg>
                    </div>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-flex>

          <!-- RIGHT COLUMN: Timer + Settings -->
          <v-flex xs12 md6>
            <v-layout column>
              <!-- Timer -->
              <v-flex>
                <v-card class="theme-bg-card-2 card-animate-2">
                  <v-card-title class="card-head">
                    <div class="card-title">Timer</div>
                    <div class="legend">
                      <span class="kbd">Space</span> start/pause • <span class="kbd">R</span> reset • 1/2/3 switch
                    </div>
                  </v-card-title>

                  <v-card-text>
                    <div class="timer-panel">
                      <div class="dial" aria-live="polite">
                        <div class="mode-chip">{{ modeLabel }}</div>
                        <svg class="ring" width="100%" height="100%" viewBox="0 0 220 220" style="position:absolute;inset:0;margin:auto">
                          <defs>
                            <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0%"   stop-color="var(--accent)"/>
                              <stop offset="50%"  stop-color="var(--accent-2)"/>
                              <stop offset="100%" stop-color="var(--accent-3)"/>
                            </linearGradient>
                          </defs>
                          <circle cx="110" cy="110" r="94" fill="none" stroke="rgba(255,255,255,.06)" stroke-width="20"/>
                          <circle
                            :stroke-dasharray="ringDashArray"
                            cx="110" cy="110" r="94" fill="none" stroke="url(#grad)" stroke-width="20"
                            stroke-linecap="round" transform="rotate(-90 110 110)"
                          />
                        </svg>
                        <div class="time">{{ timeLabel }}</div>
                        <div class="progress-label">
                          <span>{{ elapsedLabel }}</span><span>/ {{ totalLabel }}</span>
                        </div>
                      </div>

                      <div>
                        <div class="segmented" role="tablist" aria-label="Modes">
                          <div class="seg" :class="{active: state.mode==='focus'}" role="tab" :aria-selected="String(state.mode==='focus')" @click="setMode('focus')">Focus</div>
                          <div class="seg" :class="{active: state.mode==='short'}" role="tab" :aria-selected="String(state.mode==='short')" @click="setMode('short')">Short Break</div>
                          <div class="seg" :class="{active: state.mode==='long'}"  role="tab" :aria-selected="String(state.mode==='long')"  @click="setMode('long')">Long Break</div>
                        </div>

                        <div class="controls-4">
                          <button class="btn primary" :disabled="state.running" @click="start">Start</button>
                          <button class="btn" :disabled="!state.running" @click="pause">Pause</button>
                          <button class="btn" @click="resetTimer">Reset</button>
                          <button class="btn ghost" @click="skip">Skip</button>
                        </div>

                        <div class="tog">
                          <div>Auto-start next</div>
                          <div
                            class="switch"
                            :class="{ on: settings.autoStart }"
                            role="switch"
                            :aria-checked="String(!!settings.autoStart)"
                            tabindex="0"
                            @click="toggleAutoStart"
                            @keydown.enter.prevent="toggleAutoStart"
                            @keydown.space.prevent="toggleAutoStart"
                          />
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-flex>

              <!-- Settings -->
              <v-flex>
                <v-card class="theme-bg-card-2 card-animate-2">
                  <v-card-title class="card-head">
                    <div class="card-title">Settings & Sessions</div>
                  </v-card-title>

                  <v-card-text>
                    <div class="settings-grid mb-4">
                      <div>
                        <label>Focus (minutes)</label>
                        <input type="number" v-model.number="focusMin" min="1" max="180" step="1" />
                      </div>
                      <div>
                        <label>Short Break (minutes)</label>
                        <input type="number" v-model.number="shortMin" min="1" max="60" step="1" />
                      </div>
                      <div>
                        <label>Long Break (minutes)</label>
                        <input type="number" v-model.number="longMin" min="1" max="90" step="1" />
                      </div>
                      <div>
                        <label>Long Break every N Focus</label>
                        <input type="number" v-model.number="settings.longEvery" min="2" max="12" step="1" />
                      </div>
                    </div>

                    <div class="settings-grid">
                      <div>
                        <label>Beep Volume</label>
                        <input type="range" v-model.number="settings.volume" min="0" max="1" step="0.01">
                        <small class="hint">A short beep plays on completion.</small>
                      </div>
                      <div class="center">
                        <button class="btn" @click="saveSettings">Save Settings</button>
                      </div>
                      <div class="center">
                        <button class="btn" @click="exportCSV">Export CSV</button>
                      </div>
                      <div class="center">
                        <button class="btn warn" @click="clearLog">Clear Log</button>
                      </div>
                      <div class="coach-actions" style="margin-top:8px">
  <button class="btn warn" @click="clearMeetingSession">Clear Meeting Session</button>
  <button class="btn" @click="clearCallSession">Clear Call Session</button>
</div>

                    </div>
                  </v-card-text>
                </v-card>
                            <v-card class="theme-bg-card-2 card-animate-2">
              <v-card-title class="card-head">
                <div class="card-title">Live Assistant</div>
                <div class="legend legend-gap">
                  <span class="hint" :class="{ live: meetingListening }">
                    ● {{ meetingListening ? (meetingMode==='call' ? 'Listening (Call)' : 'Listening (Meeting)') : 'Idle' }}
                  </span>
                </div>
              </v-card-title>

              <v-card-text>
                <div class="meeting-controls">
                  <button class="btn primary" @click="startMeeting('meeting')" :disabled="meetingListening">Listen to Meeting</button>
                  <button class="btn primary" @click="startMeeting('call')" :disabled="meetingListening">Listen to Call</button>
                  <button class="btn" @click="stopMeeting" :disabled="!meetingListening">Stop</button>

                  <div class="tog">
                    <div>Suggest every</div>
                    <select class="interval-select" v-model.number="suggestEverySec">
                      <option :value="8">8s</option>
                      <option :value="12">12s</option>
                      <option :value="20">20s</option>
                      <option :value="30">30s</option>
                    </select>
                    <div> • Silence gap</div>
                    <select class="interval-select" v-model.number="silenceGapSec">
                      <option :value="3">3s</option>
                      <option :value="5">5s</option>
                      <option :value="8">8s</option>
                    </select>
                  </div>
                </div>

                <div class="ai-answer" v-if="lastSuggestions.length">
                  <div style="font-weight:800; margin-bottom:8px">Latest Suggestions</div>
                  <ul style="margin:0 0 8px 18px">
                    <li v-for="(s, i) in lastSuggestions.slice(0,3)" :key="'sug-'+i">{{ s }}</li>
                  </ul>
                  <div class="coach-actions">
                    <button class="btn" @click="acceptSuggestion(lastSuggestions[0])" :disabled="!lastSuggestions.length">Use Top Suggestion</button>
                    <button class="btn ghost" @click="clearLatestSuggestions" :disabled="!lastSuggestions.length">Clear</button>
                  </div>
                </div>
              </v-card-text>
            </v-card>
              </v-flex>
            </v-layout>
          </v-flex>

          <!-- Row: Meeting Assistant + Session Log -->
          <!-- <v-flex xs12 md6>
            <v-card class="theme-bg-card-2 card-animate-2">
              <v-card-title class="card-head">
                <div class="card-title">Live Assistant</div>
                <div class="legend legend-gap">
                  <span class="hint" :class="{ live: meetingListening }">
                    ● {{ meetingListening ? (meetingMode==='call' ? 'Listening (Call)' : 'Listening (Meeting)') : 'Idle' }}
                  </span>
                </div>
              </v-card-title>

              <v-card-text>
                <div class="meeting-controls">
                  <button class="btn primary" @click="startMeeting('meeting')" :disabled="meetingListening">Listen to Meeting</button>
                  <button class="btn primary" @click="startMeeting('call')" :disabled="meetingListening">Listen to Call</button>
                  <button class="btn" @click="stopMeeting" :disabled="!meetingListening">Stop</button>

                  <div class="tog">
                    <div>Suggest every</div>
                    <select class="interval-select" v-model.number="suggestEverySec">
                      <option :value="8">8s</option>
                      <option :value="12">12s</option>
                      <option :value="20">20s</option>
                      <option :value="30">30s</option>
                    </select>
                    <div> • Silence gap</div>
                    <select class="interval-select" v-model.number="silenceGapSec">
                      <option :value="3">3s</option>
                      <option :value="5">5s</option>
                      <option :value="8">8s</option>
                    </select>
                  </div>
                </div>

                <div class="ai-answer" v-if="lastSuggestions.length">
                  <div style="font-weight:800; margin-bottom:8px">Latest Suggestions</div>
                  <ul style="margin:0 0 8px 18px">
                    <li v-for="(s, i) in lastSuggestions.slice(0,3)" :key="'sug-'+i">{{ s }}</li>
                  </ul>
                  <div class="coach-actions">
                    <button class="btn" @click="acceptSuggestion(lastSuggestions[0])" :disabled="!lastSuggestions.length">Use Top Suggestion</button>
                    <button class="btn ghost" @click="clearLatestSuggestions" :disabled="!lastSuggestions.length">Clear</button>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-flex> -->

          <v-flex xs12>
            <v-card class="theme-bg-card-2 card-animate-2">
              <v-card-title class="card-head">
                <div class="card-title">Session Log</div>
                <div class="legend">
                  <span class="hint">Tip: Customize durations above. State & theme are saved.</span>
                </div>
              </v-card-title>

              <v-card-text>
                <div class="log scroll" aria-label="Session log">
                  <table>
                    <thead>
                      <tr><th style="width:32%">Date</th><th>Type</th><th>Duration</th><th>Result</th><th style="width:64px"></th></tr>
                    </thead>
                    <tbody>
                      <tr v-if="!log.length">
                        <td colspan="5" class="empty-log">No sessions yet. Let's get some focus energy!</td>
                      </tr>
                      <tr v-for="entry in reversedLog" :key="entry.id">
                        <td>{{ formatDate(entry.endedAt || entry.startedAt) }}</td>
                        <td>
                          <span class="tag" :class="entry.type==='focus' ? 'focus' : (entry.type==='short' ? 'short' : 'long')">
                            {{ entry.type==='focus'?'Focus':(entry.type==='short'?'Short Break':'Long Break') }}
                          </span>
                        </td>
                        <td>{{ formatTime(entry.durationSec) }}</td>
                        <td>
                          <span :style="{color: entry.completed ? 'var(--ok)' : 'var(--danger)', fontWeight:700}">
                            {{ entry.completed ? 'Done' : 'Skipped' }}
                          </span>
                        </td>
                        <td><button class="btn ghost" @click="deleteLog(entry.id)" title="Delete">✕</button></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </v-card-text>
            </v-card>
          </v-flex>

          <!-- Conversation Log -->
          <v-flex xs12>
            <v-card class="theme-bg-card-2 card-animate-2">
              <v-card-title class="card-head">
                <div class="card-title">Conversation Log</div>
                <div class="actions">
                  <button class="btn ghost" @click="exportConvLogCSV" :disabled="!conversationLog.length">Export CSV</button>
                </div>
              </v-card-title>

              <v-card-text>
                <div class="log scroll" aria-label="Conversation log">
                  <table>
                    <thead>
                      <tr><th style="width:24%">Time</th><th style="width:14%">Speaker</th><th>Text</th></tr>
                    </thead>
                    <tbody>
                      <tr v-if="!conversationLog.length">
                        <td colspan="3" class="empty-log">No conversation captured yet.</td>
                      </tr>
                      <tr v-for="(m, i) in conversationLog" :key="'msg-'+i">
                        <td>{{ formatDate(m.ts) }}</td>
                        <td><span class="tag" :class="m.role">{{ m.role }}</span></td>
                        <td>{{ m.text }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </v-card-text>
            </v-card>
          </v-flex>
          
      <!-- Floating suggestion popup -->
      <transition name="suggest-pop">
        <div v-if="suggestPopup.visible" class="suggest-popup" role="dialog" aria-live="polite">
          <div class="suggest-title">Suggested reply</div>
          <div class="suggest-body">{{ suggestPopup.text }}</div>
          <div class="coach-actions">
            <button class="btn primary" @click="acceptSuggestion(suggestPopup.text)">Use</button>
            <button class="btn" @click="copySuggestion(suggestPopup.text)">Copy</button>
            <button class="btn ghost" @click="dismissPopup">Dismiss</button>
          </div>
        </div>
      </transition>

      <!-- Bottom dock: accepted suggestions history -->
      <div class="assistant-dock" v-if="acceptedSuggestions.length">
        <div class="dock-title">Assistant Suggestions (sent/kept)</div>
        <div class="dock-chips">
          <span v-for="(s, i) in acceptedSuggestions" :key="'dock-'+i" class="chip" :title="s">{{ s }}</span>
        </div>
      </div>
        </v-layout>
      </v-container>

    </div>
  </v-main>
</template>


<script>
export default {
  name: "AITimer",
  data() {
    return {
      // theme
      isDark: true,

      // settings
      settings: {
        focus: 25 * 60,
        shortBreak: 5 * 60,
        longBreak: 15 * 60,
        longEvery: 4,
        autoStart: true,
        volume: 0.4,
      },

      // state
      state: {
        running: false,
        mode: "focus", // 'focus' | 'short' | 'long'
        sessionCount: 0,
        startAt: null,
        endAt: null,
        remaining: null,
        tickTimer: null,
        label: "", // optional: user-specified task label via voice
      },

      // log
      log: [],

      // coach
      coachQuestion: "",
      coachAnswer: "",
      coachLoading: false,

      // voice
      isListening: false,
      recognition: null,
      lastVoiceText: "",

      // chart
      chartW: 660,
      chartH: 180,
     meetingListening: false,
    meetingMode: 'meeting', // 'meeting' | 'call'
    conversationLog: [],    // {role: 'speaker'|'you'|'assistant', text, ts}
    lastUtteranceAt: 0,
    lastSuggestAt: 0,
    suggestTimer: null,
    silenceTimer: null,
    suggestEverySec: 12,
    silenceGapSec: 5,
    lastSuggestions: [],
    suggestPopup: {
      visible: false,
      text: ''
    },
    acceptedSuggestions: [],
      // OpenAI config
      openaiModel: "gpt-4o",
      openaiKey: process.env.VUE_APP_OPENAI_API_KEY || "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", // Prefer backend proxy in prod
      openaiEndpoint: process.env.VUE_APP_OPENAI_ENDPOINT || "https://api.openai.com/v1/chat/completions",
      enableBreakNudges: true, // suggest micro-tasks when a break starts
    };
  },

  computed: {
    focusMin: {
      get() { return Math.round(this.settings.focus / 60); },
      set(v) { this.settings.focus = this.sec(this.clampInt(v, 1, 180)); }
    },
    shortMin: {
      get() { return Math.round(this.settings.shortBreak / 60); },
      set(v) { this.settings.shortBreak = this.sec(this.clampInt(v, 1, 60)); }
    },
    longMin: {
      get() { return Math.round(this.settings.longBreak / 60); },
      set(v) { this.settings.longBreak = this.sec(this.clampInt(v, 1, 90)); }
    },

    modeLabel() {
      return this.state.mode === "focus" ? (this.state.label ? `Focus • ${this.state.label}` : "Focus")
           : this.state.mode === "short" ? "Short Break"
           : "Long Break";
    },
    totalSeconds() { return this.getModeSeconds(this.state.mode); },
    timeLabel() {
      const rem = Math.max(0, this.state.remaining == null ? this.totalSeconds : this.state.remaining);
      return this.formatTime(rem);
    },
    totalLabel() { return this.formatTime(this.totalSeconds); },
    elapsedLabel() {
      const rem = Math.max(0, this.state.remaining == null ? this.totalSeconds : this.state.remaining);
      return this.formatTime(this.totalSeconds - rem);
    },
    ringDashArray() {
      const C = 2 * Math.PI * 94; // r = 94
      const rem = Math.max(0, this.state.remaining == null ? this.totalSeconds : this.state.remaining);
      const ratio = (this.totalSeconds - rem) / Math.max(1, this.totalSeconds);
      const dash = Math.max(0, C * ratio);
      return `${dash} ${C}`;
    },
    reversedLog() { return this.log.slice().reverse(); },
  },

  methods: {
    /* ---------- Utils ---------- */
    clampInt(v, min, max) { v = isFinite(v) ? Math.round(v) : min; return Math.min(max, Math.max(min, v)); },
    sec(mins) { return Math.max(1, Math.round(mins * 60)); },
    formatTime(sec) { sec = Math.max(0, Math.round(sec)); const m = Math.floor(sec/60); const s = sec % 60; return `${m}:${String(s).padStart(2,"0")}`; },
    formatDate(iso) { const d = new Date(iso); return d.toLocaleString([], {weekday:'short', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'}); },

    saveSettings() {
      localStorage.setItem("pomodoro_settings_v1", JSON.stringify(this.settings));
      if (!this.state.running) this.state.remaining = this.getModeSeconds(this.state.mode);
      this.flashBeep();
    },
    exportCSV() {
      const header = ["Date","Type","Duration (sec)","Completed"];
      const lines = [header.join(",")];
      this.log.forEach(r=>{
        const time = new Date(r.endedAt||r.startedAt).toLocaleString();
        lines.push([`"${time}"`, r.type, r.durationSec, r.completed?"yes":"no"].join(","));
      });
      const blob = new Blob([lines.join("\n")], {type:"text/csv;charset=utf-8"});
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = "pomodoro_log.csv"; a.click(); URL.revokeObjectURL(url);
    },
    
    clearLog() {
      if (confirm("Clear all session logs?")) {
        this.log = [];
        localStorage.setItem("pomodoro_log_v1", JSON.stringify(this.log));
        this.renderChart();
      }
    },

    toggleTheme() {
      this.isDark = !this.isDark;
      localStorage.setItem("pomodoro_theme_v1", this.isDark ? "dark" : "light");
      this.$nextTick(()=> setTimeout(this.renderChart, 60));
    },

    /* ---------- Mode & Controls ---------- */
    getModeSeconds(mode){ return mode==="focus" ? this.settings.focus : (mode==="short" ? this.settings.shortBreak : this.settings.longBreak); },
    setMode(mode) {
      if (this.state.running && !confirm("Switch mode? The current timer will be reset.")) return;
      this.state.mode = mode;
      this.state.running = false;
      this.state.endAt = null;
      this.state.remaining = this.getModeSeconds(mode);
      if (mode !== "focus") this.state.label = "";
      this.persistState();
    },
    start() {
      if (this.state.running) return;
      const now = Date.now();
      const remaining = this.state.remaining == null ? this.getModeSeconds(this.state.mode) : this.state.remaining;
      this.state.endAt = now + remaining * 1000;
      this.state.startAt = now;
      this.state.running = true;
      this.tick();
      this.state.tickTimer = setInterval(this.tick, 250);
      this.persistState();
      this.beep(120, 880, 0.05);
    },
    pause() {
      if (!this.state.running) return;
      this.state.running = false;
      this.state.remaining = Math.max(0, Math.round((this.state.endAt - Date.now()) / 1000));
      clearInterval(this.state.tickTimer);
      this.persistState();
      this.beep(90, 660, 0.04);
    },
    resetTimer() {
      clearInterval(this.state.tickTimer);
      this.state.running = false;
      this.state.endAt = null;
      this.state.remaining = this.getModeSeconds(this.state.mode);
      this.persistState();
    },
    skip() {
      if (this.state.running && !confirm("Skip the rest of this session?")) return;
      this.complete(false);
    },
    toggleAutoStart(){ this.settings.autoStart = !this.settings.autoStart; this.saveSettings(); },

    tick() {
      const now = Date.now();
      const remaining = Math.max(0, Math.round((this.state.endAt - now) / 1000));
      this.state.remaining = remaining;
      if (remaining <= 0) { clearInterval(this.state.tickTimer); this.complete(true); }
    },

    complete(success) {
      const total = this.getModeSeconds(this.state.mode);
      const elapsed = success ? total : (total - (this.state.remaining || 0));
      if (elapsed > 0) {
        this.log.push({
          id: this.cryptoRandom(),
          type: this.state.mode,
          label: this.state.label || "",
          startedAt: new Date(Date.now() - elapsed * 1000).toISOString(),
          endedAt: new Date().toISOString(),
          durationSec: elapsed,
          completed: !!success,
        });
        localStorage.setItem("pomodoro_log_v1", JSON.stringify(this.log));
        this.renderChart();
      }
      if (success) { this.beep(450, 1046.5); this.beep(320, 1318.5, 0.08, 160); }
      else { this.beep(240, 880, 0.06); }

      // flow
      if (this.state.mode === "focus") {
        if (success) this.state.sessionCount++;
        const isLong = this.state.sessionCount>0 && (this.state.sessionCount % this.settings.longEvery === 0);
        this.state.mode = isLong ? "long" : "short";
      } else {
        this.state.mode = "focus";
      }
      // carry label only in focus
      if (this.state.mode !== "focus") this.state.label = "";

      this.state.running = false;
      this.state.endAt = null;
      this.state.remaining = this.getModeSeconds(this.state.mode);
      this.persistState();

      // 👇 Smart nudge on breaks
      if (this.enableBreakNudges && (this.state.mode === "short" || this.state.mode === "long")) {
        this.microNudge().catch(()=>{ /* non-blocking */ });
      }

      if (this.settings.autoStart) setTimeout(this.start, 550);
    },

    deleteLog(id){ const i = this.log.findIndex(x=>x.id===id); if(i>=0){ this.log.splice(i,1); localStorage.setItem("pomodoro_log_v1", JSON.stringify(this.log)); this.renderChart(); } },

    cryptoRandom() {
      try {
        const a = new Uint32Array(2); window.crypto.getRandomValues(a);
        return [...a].map(x=>x.toString(16)).join("");
      } catch (e) {
        console.warn("crypto.getRandomValues unavailable; falling back to Math.random()", e);
        return String(Math.random()).slice(2);
      }
    },

    /* ---------- Persistence ---------- */
    persistState() {
      const persisted = {
        running: this.state.running,
        mode: this.state.mode,
        sessionCount: this.state.sessionCount,
        endAt: this.state.endAt,
        remaining: this.state.running
          ? Math.max(0, Math.round((this.state.endAt - Date.now()) / 1000))
          : this.state.remaining,
        label: this.state.label || "",
      };
      localStorage.setItem("pomodoro_state_v1", JSON.stringify(persisted));
    },

    restoreState() {
      try {
        const s = JSON.parse(localStorage.getItem("pomodoro_settings_v1"));
        if (s) Object.assign(this.settings, s);
      } catch (e) {
        console.warn("Failed to load settings from localStorage", e);
      }
      try {
        const lg = JSON.parse(localStorage.getItem("pomodoro_log_v1"));
        if (Array.isArray(lg)) this.log = lg;
      } catch (e) {
        console.warn("Failed to load session log from localStorage", e);
      }
      try {
        const st = JSON.parse(localStorage.getItem("pomodoro_state_v1"));
        if (st) {
          this.state.mode = st.mode || "focus";
          this.state.sessionCount = st.sessionCount || 0;
          this.state.label = st.label || "";
          if (st.running && st.endAt && st.endAt > Date.now()) {
            this.state.running = true;
            this.state.endAt = st.endAt;
            this.state.remaining = Math.round((st.endAt - Date.now()) / 1000);
            this.state.tickTimer = setInterval(this.tick, 250);
          } else {
            this.state.running = false;
            this.state.endAt = null;
            this.state.remaining = typeof st.remaining === "number" ? st.remaining : this.getModeSeconds(this.state.mode);
          }
        } else {
          this.state.remaining = this.getModeSeconds(this.state.mode);
        }
      } catch (e) {
        console.warn("Failed to load persisted state from localStorage", e);
        this.state.running = false;
        this.state.endAt = null;
        this.state.remaining = this.getModeSeconds(this.state.mode);
      }
      const savedTheme = localStorage.getItem("pomodoro_theme_v1");
      this.isDark = savedTheme ? (savedTheme === "dark") : !!(window.matchMedia && matchMedia("(prefers-color-scheme: dark)").matches);
    },

    /* ---------- Beep (WebAudio) ---------- */
    beep(duration = 400, freq = 880, vol = this.settings.volume, delay = 0) {
      try {
        if (!this._audioCtx) this._audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const now = this._audioCtx.currentTime + (delay / 1000);
        const o = this._audioCtx.createOscillator();
        const g = this._audioCtx.createGain();
        o.frequency.value = freq; o.type = "sine";
        g.gain.setValueAtTime(0.0001, now);
        g.gain.exponentialRampToValueAtTime(Math.max(0.0002, vol), now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + duration / 1000);
        o.connect(g).connect(this._audioCtx.destination);
        o.start(now); o.stop(now + duration / 1000 + 0.05);
      } catch (e) {
        console.warn("WebAudio beep failed (autoplay policy or missing AudioContext):", e);
      }
    },
    flashBeep() { this.beep(120, 880, 0.04); },

    /* ---------- Chart (SVG) ---------- */
    renderChart() {
      const el = this.$refs.chart; if (!el) return;
      const days = this.lastNDays(14);
      const counts = days.map(d => this.log.filter(x => x.type==="focus" && x.completed && (x.endedAt||x.startedAt).slice(0,10)===d).length);
      const max = Math.max(4, ...counts);
      const W=this.chartW, H=this.chartH, padL=38, padB=26, padR=10, padT=10;
      const innerW=W-padL-padR, innerH=H-padT-padB; const bw=innerW/days.length;
      let svg="";
      for(let i=0;i<=max;i++){
        const y=padT+innerH-(i/max)*innerH; const op=i===0?.25:.10;
        svg += `<line x1="${padL}" y1="${y.toFixed(1)}" x2="${W-padR}" y2="${y.toFixed(1)}" stroke="rgba(255,255,255,${op})" />`;
        if(i%2===0){ svg += `<text x="${padL-8}" y="${(y+4).toFixed(1)}" text-anchor="end" font-size="10" fill="var(--muted)">${i}</text>`; }
      }
      counts.forEach((c,i)=>{
        const x=padL+i*bw+8; const h=(c/max)*innerH; const y=padT+innerH-h; const w=Math.max(10, bw-16);
        const dstr=days[i].slice(5).replace("-", "/");
        svg += `<title>${dstr}: ${c} focus</title>
          <rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="6" ry="6" fill="url(#gradBar)"></rect>
          <text x="${(x+w/2).toFixed(1)}" y="${(H-8)}" text-anchor="middle" font-size="10" fill="var(--muted)">${dstr}</text>`;
      });
      const defs = `<defs>
        <linearGradient id="gradBar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="var(--accent)"/>
          <stop offset="50%" stop-color="var(--accent-2)"/>
          <stop offset="100%" stop-color="rgba(255,255,255,.15)"/>
        </linearGradient>
      </defs>`;
      el.innerHTML = defs + svg;
    },
    lastNDays(n){ const out=[]; const t=new Date(); for(let i=n-1;i>=0;i--){ const d=new Date(t); d.setDate(t.getDate()-i); out.push(d.toISOString().slice(0,10)); } return out; },

    /* ---------- Keyboard ---------- */
    // bindShortcuts() {
    //   document.addEventListener("keydown", this._keyHandler = (e) => {
    //     const active = document.activeElement && ["INPUT","TEXTAREA"].includes(document.activeElement.tagName);
    //     if (active) return;
    //     if (e.key === " ") { e.preventDefault(); this.state.running ? this.pause() : this.start(); }
    //     if (e.key === "r" || e.key === "R") { e.preventDefault(); this.resetTimer(); }
    //     if (e.key === "1") this.setMode("focus");
    //     if (e.key === "2") this.setMode("short");
    //     if (e.key === "3") this.setMode("long");
    //   });
    // },

    /* ---------- Voice (Web Speech API) ---------- */
    // initSpeech() {
    //   const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    //   if (!SR) return; // feature detect
    //   this.recognition = new SR();
    //   this.recognition.lang = "en-US";
    //   this.recognition.interimResults = false;
    //   this.recognition.continuous = true;

    //   this.recognition.onresult = (ev) => {
    //     const idx = ev.results.length - 1;
    //     const transcript = ev.results[idx][0].transcript.trim();
    //     this.lastVoiceText = transcript;
    //     if (this.handleVoiceCommand(transcript)) return;
    //   };
    //   this.recognition.onend = () => { if (this.isListening) this.recognition.start(); };
    // },
    initSpeech() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return; // feature detect
  this.recognition = new SR();
  this.recognition.lang = "en-US";
  this.recognition.interimResults = false;
  this.recognition.continuous = true;

  this.recognition.onresult = (ev) => {
    const idx = ev.results.length - 1;
    const transcript = ev.results[idx][0].transcript.trim();
    this.lastVoiceText = transcript;

    if (this.meetingListening) {
      this.ingestUtterance(transcript); // feed meeting/call pipeline
      return;
    }
    // normal command mode
    if (this.handleVoiceCommand(transcript)) return;
  };

  this.recognition.onend = () => {
    // keep alive if listening in either mode
    if (this.isListening || this.meetingListening) this.recognition.start();
  };
},
  /* ---------- Live Meeting / Call ---------- */
  startMeeting(mode = 'meeting') {
    if (!this.recognition) { alert("Speech recognition not supported in this browser."); return; }
    this.meetingMode = mode;
    this.meetingListening = true;
    this.isListening = false; // prevent command mode conflicts
    this.recognition.start();
    this.lastUtteranceAt = Date.now();
    this.lastSuggestAt = 0;
    this.queueSuggest(); // prime timer
  },
  stopMeeting() {
    this.meetingListening = false;
    this.clearSuggestTimers();
  },
  ingestUtterance(text) {
    // Treat all captured speech as from "speaker" (you can add diarization later)
    this.conversationLog.push({ role: 'speaker', text, ts: new Date().toISOString() });
    this.persistConvLog();
    this.lastUtteranceAt = Date.now();
    this.queueSuggest();
  },
  queueSuggest() {
    // Periodic suggestions
    if (this.suggestTimer) clearTimeout(this.suggestTimer);
    const now = Date.now();

    // If enough seconds elapsed since last suggestion, fire soon; else wait
    const elapsedSinceSuggest = (now - this.lastSuggestAt) / 1000;
    const waitA = Math.max(0, this.suggestEverySec - elapsedSinceSuggest);

    // Also honor a silence gap—only suggest after a short pause
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    const sinceLastSpeech = (now - this.lastUtteranceAt) / 1000;
    const gap = Math.max(0, this.silenceGapSec - sinceLastSpeech);

    const wait = Math.max(waitA, gap);
    this.suggestTimer = setTimeout(this.suggestIfNeeded, wait * 1000);
  },
  clearSuggestTimers() {
    if (this.suggestTimer) clearTimeout(this.suggestTimer);
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.suggestTimer = null;
    this.silenceTimer = null;
  },
  async suggestIfNeeded() {
    if (!this.meetingListening) return;
    const now = Date.now();
    const sinceLast = (now - this.lastSuggestAt) / 1000;
    if (sinceLast < this.suggestEverySec - 0.5) return; // throttle

    const ctx = this.buildConversationContext(8); // last N messages
    if (!ctx.trim()) { this.queueSuggest(); return; }

    try {
      const suggestions = await this.getSuggestionsFromAI(ctx, this.meetingMode);
      this.lastSuggestions = suggestions.slice(0, 3);
      if (this.lastSuggestions.length) {
        this.showPopup(this.lastSuggestions[0]);
        // Also add a synthetic assistant log line
        this.conversationLog.push({ role: 'assistant', text: this.lastSuggestions[0], ts: new Date().toISOString() });
        this.persistConvLog();
      }
      this.lastSuggestAt = Date.now();
    } catch (e) {
      console.warn('Suggestion error', e);
    } finally {
      this.queueSuggest();
    }
  },
  buildConversationContext(n = 8) {
    const tail = this.conversationLog.slice(-n);
    return tail.map(m => `${m.role === 'you' ? 'You' : (m.role === 'assistant' ? 'Assistant' : 'Speaker')}: ${m.text}`).join('\n');
  },
  async getSuggestionsFromAI(context, mode) {
    // reuse your callOpenAI; tailor the prompt for brief, speakable replies
    const persona = mode === 'call'
      ? 'You are a concise SDR/Account Executive assistant for a phone call.'
      : 'You are a concise meeting assistant for business/product discussions.';
    const prompt = `${persona}
Rules:
- Suggest 2–3 short candidate replies (<20 words each), natural and helpful.
- Avoid repeating prior content verbatim; ask a clarifying question if needed.
- If an action or promise is implied, keep it realistic.
- Return as lines starting with "- ".

Recent conversation:
${context}
`;

    const raw = await this.callOpenAI(prompt);
    // Parse "- " lines to array
    const lines = (raw || '')
      .split('\n')
      .map(s => s.trim())
      .filter(s => s.startsWith('- '))
      .map(s => s.replace(/^-+\s*/, '').trim())
      .filter(Boolean);
    // Fallback: if parsing failed, treat the whole text as one suggestion
    return lines.length ? lines : [raw].filter(Boolean);
  },

  /* ---------- Popup + Dock ---------- */
  showPopup(text) {
    this.suggestPopup.text = text;
    this.suggestPopup.visible = true;
  },
  dismissPopup() {
    this.suggestPopup.visible = false;
    this.suggestPopup.text = '';
  },
  acceptSuggestion(text) {
    if (!text) return;
    // Consider this your reply (you could also auto-copy or integrate with a chat box)
    this.conversationLog.push({ role: 'you', text, ts: new Date().toISOString() });
    this.persistConvLog();
    this.acceptedSuggestions.push(text);
    this.dismissPopup();
    // Scroll bottom dock into view
    this.$nextTick(() => {
      const el = document.querySelector('.assistant-dock');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  },
  copySuggestion(text) {
    if (!text) return;
    navigator.clipboard?.writeText(text).catch(()=>{});
  },
  clearLatestSuggestions() {
    this.lastSuggestions = [];
  },

  /* ---------- Conversation Log persistence ---------- */
 persistConvLog() {
  try {
    localStorage.setItem('conv_log_v1', JSON.stringify(this.conversationLog));
    localStorage.setItem('conv_accept_v1', JSON.stringify(this.acceptedSuggestions));
  } catch (e) {
    // Fallback: ignore persistence errors (quota exceeded, private mode, etc.)
    console.warn("Failed to persist conversation log:", e);
  }
},
restoreConvLog() {
  try {
    const a = JSON.parse(localStorage.getItem('conv_log_v1'));
    if (Array.isArray(a)) this.conversationLog = a;

    const b = JSON.parse(localStorage.getItem('conv_accept_v1'));
    if (Array.isArray(b)) this.acceptedSuggestions = b;
  } catch (e) {
    // Fallback: ignore load errors
    console.warn("Failed to restore conversation log:", e);
  }
},

  exportConvLogCSV() {
    const header = ['Time','Role','Text'];
    const lines = [header.join(',')];
    this.conversationLog.forEach(m=>{
      const t = new Date(m.ts || Date.now()).toLocaleString();
      const row = [`"${t}"`, m.role, `"${(m.text||'').replace(/"/g,'""')}"`];
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], {type:'text/csv;charset=utf-8'});
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = 'conversation_log.csv'; a.click(); URL.revokeObjectURL(url);
  },
    toggleListening() {
      if (!this.recognition) { alert("Speech recognition not supported in this browser."); return; }
      this.isListening = !this.isListening;
      if (this.isListening) this.recognition.start(); else this.recognition.stop();
    },
    speakHint() {
      const s = window.speechSynthesis; if (!s) return;
      const u = new SpeechSynthesisUtterance("Try saying: Start a 25 minute timer for Website Development Project.");
      s.speak(u);
    },

    handleVoiceCommand(text) {
      const t = text.toLowerCase();
      const startRe = /(start|begin)\s+(a\s+)?(\d+)\s*(minute|min|minutes)\s*(timer)?(\s*(for|to track|to)\s+(.*))?$/i;
      const m = t.match(startRe);
      if (m) {
        const minutes = parseInt(m[3], 10);
        const label = (m[8] || "").trim();
        this.state.mode = "focus";
        this.state.label = label ? this.titleCase(label) : "";
        this.settings.focus = this.sec(this.clampInt(minutes, 1, 180));
        this.state.remaining = this.settings.focus;
        this.start();
        this.say(`Starting a ${minutes} minute focus timer ${label ? "for " + label : ""}.`);
        return true;
      }
      if (/(pause|hold)\s+(timer|countdown)?$/.test(t)) { this.pause(); this.say("Paused."); return true; }
      if (/(resume|continue|unpause)\s+(timer|countdown)?$/.test(t)) { this.start(); this.say("Resumed."); return true; }
      if (/(reset|restart)\s+(timer|countdown)?$/.test(t)) { this.resetTimer(); this.say("Reset."); return true; }
      if (/skip\s+(break|session|timer)?$/.test(t)) { this.skip(); this.say("Skipped."); return true; }
      if (/(switch|set)\s+to\s+(short\s*break)/.test(t)) { this.setMode("short"); this.say("Short break."); return true; }
      if (/(switch|set)\s+to\s+(long\s*break)/.test(t)) { this.setMode("long"); this.say("Long break."); return true; }
      if (/(switch|set)\s+to\s+focus/.test(t)) { this.setMode("focus"); this.say("Focus mode."); return true; }
      if (/(dark\s*mode\s*(on|enable))/.test(t)) { if (!this.isDark) this.toggleTheme(); this.say("Dark mode on."); return true; }
      if (/(dark\s*mode\s*(off|disable)|light\s*mode)/.test(t)) { if (this.isDark) this.toggleTheme(); this.say("Light mode on."); return true; }
      return false;
    },
    say(text) { const s = window.speechSynthesis; if (!s) return; s.speak(new SpeechSynthesisUtterance(text)); },
    titleCase(s) { return s.replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1)); },

    /* ---------- AI Coach ---------- */
    summaryForCoach() {
      const days = this.lastNDays(14);
      const focusByDay = days.map(d => ({
        date: d,
        sessions: this.log
          .filter(x => x.type === "focus" && (x.endedAt || x.startedAt).slice(0,10) === d)
          .map(x => ({ dur: x.durationSec, done: x.completed, label: x.label || "" }))
      }));
      const topLabels = {};
      this.log.forEach(x => { if (x.label) topLabels[x.label] = (topLabels[x.label] || 0) + (x.completed ? x.durationSec : 0); });
      const top = Object.entries(topLabels).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([k,v])=>({label:k, seconds:v}));
      return {
        focusByDay,
        topLabels: top,
        settings: {
          focusMin: Math.round(this.settings.focus/60),
          shortBreakMin: Math.round(this.settings.shortBreak/60),
          longBreakMin: Math.round(this.settings.longBreak/60),
          longEvery: this.settings.longEvery
        }
      };
    },

    async askCoach() {
      const q = this.coachQuestion.trim(); if (!q) return;
      this.coachLoading = true;
      try {
        const prompt = `You are a work-habits coach. Analyze the JSON summary of the user's timer sessions and answer the question.

Rules:
- Be concrete and concise.
- Use bullet points when helpful.
- If a timer would help, recommend an exact duration and label.
- If you propose a routine, keep it realistic.

User question: ${q}

JSON summary:
${JSON.stringify(this.summaryForCoach())}`;
        const answer = await this.callOpenAI(prompt);
        this.coachAnswer = this.linkify(answer);
      } catch (e) {
        console.error(e);
        this.coachAnswer = "Sorry, I couldn't analyze that just now.";
      } finally {
        this.coachLoading = false;
      }
    },

    async applyVoiceInsights() {
      if (!this.lastVoiceText) return;
      this.coachLoading = true;
      try {
        const prompt = `User said: "${this.lastVoiceText}".

Given this and the JSON summary of recent sessions below, suggest:
- 1-2 specific, actionable next steps,
- and if appropriate, a timer: {minutes, label}.

Return a brief explanation first, then a single line "TIMER: <minutes>|<label>" if a timer makes sense.

JSON summary:
${JSON.stringify(this.summaryForCoach())}`;
        const answer = await this.callOpenAI(prompt);
        this.coachAnswer = this.linkify(answer);

        const match = /TIMER:\s*(\d+)\s*\|\s*(.+)$/i.exec(answer);
        if (match) {
          const minutes = parseInt(match[1], 10);
          const label = match[2].trim();
          this.state.mode = "focus";
          this.state.label = label;
          this.settings.focus = this.sec(this.clampInt(minutes, 1, 180));
          this.state.remaining = this.settings.focus;
          this.start();
          this.say(`Starting a ${minutes} minute focus timer for ${label}.`);
        }
      } catch (e) {
        console.error(e);
        this.coachAnswer = "Couldn't process that voice note. Try again?";
      } finally {
        this.coachLoading = false;
      }
    },

    // Suggest a tiny action when a break begins
    async microNudge() {
      try {
        const prompt = `Suggest one ultra-short (2–5 min) action I can do during a break based on this summary.
Return 2 bullet points max. Prefer inbox triage, stretch, hydration, or prep for next meeting if patterns suggest it.

JSON summary:
${JSON.stringify(this.summaryForCoach())}`;
        const answer = await this.callOpenAI(prompt);
        if (answer) this.coachAnswer = this.linkify(answer);
      } catch (e) {
        // soft fail OK
      }
    },

    async callOpenAI(prompt) {
      if (!this.openaiKey) throw new Error("Missing OpenAI key. Set VUE_APP_OPENAI_API_KEY or proxy requests.");
      const res = await fetch(this.openaiEndpoint, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${this.openaiKey}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          model: this.openaiModel,
          messages: [
            { role: "system", content: "You are a thoughtful productivity coach." },
            { role: "user", content: prompt }
          ],
          temperature: 0.3,
          max_tokens: 800
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || "OpenAI error");
      return data.choices?.[0]?.message?.content?.trim() || "";
    },

    linkify(text) { return (text || "").replace(/(https?:\/\/[^\s)]+)\b/gi, '<a href="$1" target="_blank" rel="noopener">$1</a>'); },

    /* ---------- Chart + shortcuts ---------- */
    bindShortcuts() {
      document.addEventListener("keydown", this._keyHandler = (e) => {
        const active = document.activeElement && ["INPUT","TEXTAREA"].includes(document.activeElement.tagName);
        if (active) return;
        if (e.key === " ") { e.preventDefault(); this.state.running ? this.pause() : this.start(); }
        if (e.key === "r" || e.key === "R") { e.preventDefault(); this.resetTimer(); }
        if (e.key === "1") this.setMode("focus");
        if (e.key === "2") this.setMode("short");
        if (e.key === "3") this.setMode("long");
      });
    },
  },

  mounted() {
  this.restoreState();
  this.restoreConvLog(); // NEW
  this.renderChart();
  this.bindShortcuts();
  this.initSpeech();

    // gentle chirp on first pointer interaction
    window.addEventListener("pointerdown", this._welcomeOnce = () => {
      this.flashBeep();
      window.removeEventListener("pointerdown", this._welcomeOnce);
    });

    window.addEventListener("beforeunload", () => this.persistState());
  },

  beforeDestroy() {
    if (this.state.tickTimer) clearInterval(this.state.tickTimer);
    document.removeEventListener("keydown", this._keyHandler);
  },
};
</script>
<style scoped>

:root{
  --bg:#0f1223; --panel:#14172b; --text:#e8ebff; --muted:#a9afd2;
  --accent:#7c6cff; --accent-2:#00e0b8; --accent-3:#ffb703;
  --danger:#ff4d6d; --ok:#28d07f; --card:rgba(255,255,255,0.04);
  --border:rgba(255,255,255,0.10); --shadow:0 10px 30px rgba(0,0,0,.35);
}
*, *::before, *::after { box-sizing:border-box; }
html, body { height:100%; }
.wrap{
    margin-top: -100px;
  padding: 50px 50px 50px 50px;
  min-height:100vh; color:var(--text);
  background:
    radial-gradient(1200px 1200px at 10% -10%, rgba(124,108,255,.60), transparent 80%),
    radial-gradient(1200px 1200px at 110% 10%, rgba(0,224,184,.23), transparent 80%),
    radial-gradient(1000px 1000px at 50% 120%, rgba(255,183,3,.30), transparent 80%),
    var(--bg);
  transition:background .4s ease,color .2s ease;
}
[data-theme="light"]{
  --bg:#f7f8ff; --panel:#fff; --text:#0e1330; --muted:#5a6185;
  --card:rgba(10,20,60,0.03); --border:rgba(10,20,60,0.10);
  --shadow:0 10px 24px rgba(15,20,60,.08);
}

/* Masthead */
.masthead-card{ margin-bottom:14px; }
.header-grid{
  display:grid; grid-template-columns:1fr auto; gap:14px; align-items:center;
}
.brand{ display:flex; align-items:center; gap:14px; min-width:0; }
.logo{
  width:42px; height:42px; border-radius:14px; overflow:hidden;
  background: conic-gradient(from 210deg at 50% 50%, var(--accent), var(--accent-2), var(--accent-3), var(--accent));
  box-shadow: 0 8px 24px rgba(124,108,255,.4), inset 0 3px 10px rgba(255,255,255,.18);
  display:flex; align-items:center; justify-content:center;
}
.logo img{ width:100%; height:100%; object-fit:contain; }
.title{ font-weight:900; letter-spacing:.3px; font-size:clamp(22px,2.1vw,28px); }
.subtitle{ color:var(--muted); font-size:13px; }
.header-actions{ display:flex; align-items:center; gap:14px; }

.settings-grid{
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  align-items: start;
  overflow: visible;              /* no internal scrollbars */
}

/* Optional: tighten on very small screens */
@media (max-width: 480px){
  .settings-grid{
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
  }
}


/* Optional: if your items use .field wrappers, keep them consistent */
.settings-grid .field{
  min-width: 220px;
}

/* Slim scrollbar (Firefox + WebKit) */
.settings-grid{ scrollbar-width: thin; }
.settings-grid::-webkit-scrollbar{ height: 8px; }
.settings-grid::-webkit-scrollbar-thumb{ background: var(--border); border-radius: 999px; }
.settings-grid::-webkit-scrollbar-track{ background: transparent; }

/* Tweak column width on very small screens if desired */
@media (max-width: 480px){
  .settings-grid{ grid-auto-columns: minmax(240px, 1fr); }
}

/* Cards */
.theme-bg-card-2{
  background:var(--card) !important; border:1px solid var(--border);
  border-radius:22px; box-shadow:var(--shadow);
}
.card-animate-2{ transition: transform .12s ease, box-shadow .2s ease; }
.card-animate-2:hover{ transform: translateY(-1px); box-shadow: 0 16px 40px rgba(0,0,0,.35); }

.card-head{
  display:flex; align-items:center; justify-content:space-between; width:100%;
}
.card-title{ font-weight:800; font-size:16px; letter-spacing:.2px; }
.legend{ display:flex; gap:10px; align-items:center; font-size:12px; color:var(--muted); }
.dot{ width:10px; height:10px; border-radius:50% }
.dot.focus{ background:var(--accent) }

/* Buttons, toggles, inputs */
.btn{
  padding:10px 12px; border-radius:14px; border:1px solid var(--border);
  background:linear-gradient(180deg, rgba(255,255,255,.05), rgba(0,0,0,.02));
  color:var(--text); font-weight:800; letter-spacing:.3px; font-size:14px;
  cursor:pointer; user-select:none; transition:transform .08s ease, box-shadow .2s ease, background .2s ease, border-color .2s ease;
}
.btn:hover{ transform:translateY(-1px); box-shadow:0 6px 14px rgba(124,108,255,.18); }
.btn.primary{
  background:
    linear-gradient(180deg, rgba(124,108,255,.35), rgba(124,108,255,.15)),
    linear-gradient(180deg, rgba(255,255,255,.12), rgba(255,255,255,0));
  border-color:rgba(124,108,255,.45);
  box-shadow:0 10px 24px rgba(124,108,255,.25);
}
.btn.warn{ border-color:rgba(255,77,109,.45); background:linear-gradient(180deg, rgba(255,77,109,.23), rgba(255,77,109,.08)); }
.btn.ghost{ background:transparent }

.tog{ display:flex; align-items:center; gap:10px; font-size:13px; color:var(--muted); }
.switch{
  --w:46px; --h:26px; width:var(--w); height:var(--h);
  border-radius:999px; position:relative; cursor:pointer;
  background:rgba(255,255,255,.08); border:1px solid var(--border);
  transition:background .2s ease,border-color .2s ease;
}
.switch::after{
  content:""; position:absolute; top:2px; left:2px; width:22px; height:22px; border-radius:50%;
  background:linear-gradient(180deg, #fff, #dbe2ff); transform:translateX(0);
  transition:transform .18s ease; box-shadow:0 4px 10px rgba(0,0,0,.2);
}
.switch.on{ background:linear-gradient(90deg, rgba(124,108,255,.6), rgba(0,224,184,.6)); border-color:transparent; }
.switch.on::after{ transform:translateX(20px); }

.field{
  display:flex; flex-direction:column; gap:8px; padding:12px; border-radius:14px; border:1px solid var(--border);
  background:linear-gradient(180deg, rgba(255,255,255,.04), rgba(255,255,255,0));
}
.field label{ font-size:12px; color:var(--muted) }
.field input[type="number"], .field-input{
  appearance:textfield; padding:10px 12px; border-radius:10px; border:1px solid var(--border);
  background:transparent; color:var(--text); font-weight:700; font-variant-numeric:tabular-nums; outline:none; width:100%;
}
.field input[type="range"]{ width:100%; accent-color:var(--accent); }
.interval-select{
  border:1px solid var(--border); background:transparent; color:var(--text);
  padding:6px 10px; border-radius:10px; font-weight:700;
}
.kbd{ background:rgba(255,255,255,.08); border:1px solid var(--border); padding:2px 6px; border-radius:6px; font-weight:800; }
.hint{ font-size:12px; color:var(--muted); }
.hint.live{ color: var(--accent-2); font-weight:800; }

/* Timer */
.timer-panel{ display:grid; gap:18px; grid-template-columns:1fr 1fr; align-items:stretch; }
@media (max-width:860px){ .timer-panel{ grid-template-columns:1fr; } }
.dial{
  position:relative; aspect-ratio:1/1; display:grid; place-items:center;
  border-radius:22px; overflow:hidden;
  background:
    radial-gradient(120% 100% at 80% 10%, rgba(124,108,255,.10), transparent 60%),
    linear-gradient(160deg, rgba(255,255,255,.04), rgba(255,255,255,0));
}
.time{ font-variant-numeric:tabular-nums; font-size:clamp(42px, 6vw, 64px); font-weight:900; letter-spacing:1px;
  text-shadow:0 6px 18px rgba(0,0,0,.25); }
.mode-chip{
  position:absolute; top:14px; left:14px; font-size:12px; padding:6px 10px; border-radius:999px;
  background:linear-gradient(135deg, rgba(124,108,255,.18), rgba(0,224,184,.18));
  border:1px solid var(--border); backdrop-filter:blur(6px);
}
.progress-label{ position:absolute; bottom:14px; left:14px; right:14px; display:flex; justify-content:space-between; font-size:12px; color:var(--muted); }
.ring{ filter:drop-shadow(0 10px 30px rgba(124,108,255,.25)); }
.segmented{ display:flex; gap:8px; margin-top:6px; flex-wrap:wrap; }
.segmented .seg{
  padding:8px 12px; border-radius:999px; border:1px solid var(--border);
  font-size:12px; cursor:pointer; user-select:none; background:transparent; color:var(--muted); transition:all .2s ease;
}
.segmented .seg.active{ color:var(--text); border-color:rgba(124,108,255,.6); background:rgba(124,108,255,.18); }
.controls-4{ display:grid; gap:12px; grid-template-columns:repeat(4, minmax(0,1fr)); align-items:center; margin-top:12px; }
@media (max-width:540px){ .controls-4{ grid-template-columns:repeat(2, minmax(0,1fr)); } }

/* Meeting assistant */
.meeting-controls{
  display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:12px;
}

/* Chart */
.chart-body{ border:1px solid var(--border); border-radius:14px; padding:10px;
  background:linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,0)); }

/* AI answer block */
.ai-answer{
  border:1px solid var(--border); border-radius:14px; padding:12px;
  background:linear-gradient(180deg, rgba(255,255,255,.04), rgba(255,255,255,0)); line-height:1.5;
}
.ai-answer p{ margin:0 0 8px; }
.ai-answer ul{ margin:0 0 8px 18px; }
.ai-answer a{ color:var(--accent-2); text-decoration:none; }
.ai-answer a:hover{ text-decoration:underline; }

/* Logs / tables */
.log{ border-radius:14px; border:1px solid var(--border);
  background:linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,0)); }
.scroll{ max-height:410px; overflow:auto; }
.log table{ width:100%; border-collapse:collapse; font-size:13px; }
.log th, .log td{ padding:10px 12px; border-bottom:1px solid var(--border); text-align:left; }
.log th{
  position:sticky; top:0;
  background:linear-gradient(180deg, rgba(10,10,20,.6), rgba(10,10,20,.2));
  backdrop-filter:blur(8px); z-index:2;
}
[data-theme="light"] .log th{
  background:linear-gradient(180deg, rgba(255,255,255,.9), rgba(255,255,255,.7));
}
.empty-log{ text-align:center; color:var(--muted); padding:18px 8px; }
.tag{ font-size:11px; padding:4px 8px; border-radius:999px; border:1px solid var(--border); }
.tag.focus{ background:rgba(124,108,255,.15); border-color:rgba(124,108,255,.45) }
.tag.short{ background:rgba(00,224,184,.15); border-color:rgba(00,224,184,.45) }
.tag.long{ background:rgba(255,183,3,.15); border-color:rgba(255,183,3,.45) }
.tag.you{ background: rgba(0,224,184,.15); border-color: rgba(0,224,184,.45) }
.tag.assistant{ background: rgba(124,108,255,.15); border-color: rgba(124,108,255,.45) }

.hint.live{ color: var(--accent-2); font-weight:800; }

/* Floating suggestion popup inside card */
.suggest-popup{
  position: fixed; right: -80px; bottom: 90px; z-index: 20;
  min-width: 600px;
  max-width: 1800px;
  border:1px solid var(--border); border-radius:16px; padding:12px;
  background: linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,0.02));
  box-shadow: 0 20px 40px rgba(0,0,0,.35);
}
.suggest-title{ font-weight:800; margin-bottom:6px; }
.suggest-body{ margin-bottom:10px; line-height:1.4; }

/* Popup transition */
.suggest-pop-enter-active, .suggest-pop-leave-active { transition: all .16s ease; }
.suggest-pop-enter, .suggest-pop-leave-to { opacity:0; transform: translateY(8px); }

/* Bottom dock for accepted/logged conversation */
.assistant-dock{
  position: sticky; bottom: 0; z-index: 15;
  margin-top: 16px; padding: 10px 12px;
  border:1px solid var(--border); border-radius:16px;
  background: linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,0.02));
  backdrop-filter: blur(6px);
}
.dock-title{ font-weight:800; font-size:13px; color:var(--muted); margin-bottom:6px; }
.dock-chips{ display:flex; gap:8px; flex-wrap:wrap; }
.chip{
  display:inline-block; max-width: 360px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
  padding:6px 10px; border-radius:999px; border:1px solid var(--border);
  background: rgba(124,108,255,.15);
}
    .chip:hover{ background: rgba(124,108,255,.25); }
    .chip .actions{ display:inline-flex; gap:6px; margin-left:8px; opacity:0.6; }
    .chip .actions button{ background:transparent; border:none; color:inherit; cursor:pointer; font-size:12px; padding:0; }
    .chip .actions button:hover{ text-decoration:underline; }
</style>
